import { useEffect, useState } from "react";

import { useNavigation, useNotification, useParsed } from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { GroupForm } from "@/pages/tina/groups/components/group-form";
import { useGroupsRepository } from "@/pages/tina/groups/hooks/use-groups-repository";
import type { GroupFormType } from "@/pages/tina/groups/schemas/group.form";

export const GroupsEdit = () => {
  const { id } = useParsed();
  const groupId = id?.toString();
  const repo = useGroupsRepository();
  const { list } = useNavigation();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const setTitle = useDocumentTitle();
  const [values, setValues] = useState<GroupFormType>();

  useEffect(() => {
    if (!groupId) return;
    setLoading(true);
    repo
      .get(groupId)
      .then((group) => {
        setValues({
          name: group.name,
          time_of_day: group.time_of_day.slice(0, 5), // "HH:MM:SS" -> "HH:MM"
          days_of_week: group.days_of_week,
          sites: group.sites,
          enabled: group.enabled,
        });
        setTitle(`Edit ${group.name}`);
      })
      .catch((error) => {
        open?.({
          type: "error",
          message: "Failed to load group",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const onSubmit = async (formValues: GroupFormType) => {
    if (!groupId) return;
    try {
      await repo.update(groupId, { ...formValues, updated_by: null });
      list("tina-groups");
    } catch (error) {
      open?.({
        type: "error",
        message: "Failed to update group",
        description: toHumanErrorMessage(error),
      });
    }
  };

  return (
    <div className="flex w-full max-w-6xl flex-col gap-4 p-8">
      <div>
        <h1 className="text-2xl font-semibold">
          Edit group{values?.name ? ` · ${values.name}` : ""}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Update the schedule and membership. Times are in Europe/Athens.
        </p>
      </div>
      {values && <GroupForm defaultValues={values} submitLabel="Save" onSubmit={onSubmit} />}
    </div>
  );
};
